import { Box } from '@modusign/design-system';
import { DraggableData, DraggableEvent, DraggableProps } from 'react-draggable';
import { ResizableBox, ResizableBoxProps, ResizeCallbackData } from 'react-resizable';

import Draggable from '@app/components/Draggable';
import { AbsoluteFieldGuideline } from '@app/features/apply-react-signing/components/FieldRenderer/components/AbsoluteFieldGuideline';
import { createAbsolutePoint } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsolutePoint';
import { createAbsoluteSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsoluteSize';
import {
  AbsolutePoint,
  AbsoluteSize,
  Prettify,
} from '@app/features/apply-react-signing/components/FieldRenderer/types';

export type FieldBaseHandlers = {
  onPointChangeStart?: (point: AbsolutePoint, event: DraggableEvent) => void;
  onPointChange?: (position: AbsolutePoint, event: DraggableEvent) => void;
  onPointChangeStop?: (position: AbsolutePoint, event: DraggableEvent) => void;
  onSizeChangeStart?: (size: AbsoluteSize, event: React.SyntheticEvent) => void;
  onSizeChange?: (size: AbsoluteSize, event: React.SyntheticEvent) => void;
  onSizeChangeStop?: (size: AbsoluteSize, event: React.SyntheticEvent) => void;
};

type FieldBasePropBase = Prettify<
  Partial<Omit<DraggableProps, 'size' | 'position' | 'onStart' | 'onDrag' | 'onStop' | 'cancel'>> &
    Omit<ResizableBoxProps, 'width' | 'height' | 'onResizeStart' | 'onResizeStop' | 'onResize'> & {
      size?: AbsoluteSize;
      point: AbsolutePoint;
      dragCancelSelectors?: string[];
      restrictModification?: boolean;
      // TODO(@DevSDK, @Juyeong-Byeon): 인터페이스 관련 추후 수정 하기
      // FYI: https://github.com/modusign/modusign-client/issues/3303#issuecomment-1487992065
      disableDrag?: boolean;
      snapSize?: AbsoluteSize;
      readOnly?: boolean;
      fieldGuideline?: {
        showGuideLine: boolean;
        viewport: AbsoluteSize;
        guideLineOffset: {
          left: number;
          right: number;
          top: number;
          bottom: number;
        };
      };
      children: React.ReactNode;
    }
>;

interface FieldBaseProps extends FieldBasePropBase, FieldBaseHandlers {}

export function FieldBase(props: FieldBaseProps) {
  const {
    disableDrag,
    point,
    size = createAbsoluteSize({ width: 0, height: 0 }),
    children,
    onPointChangeStart,
    onPointChange,
    onPointChangeStop,
    onSizeChangeStart,
    onSizeChange,
    onSizeChangeStop,
    restrictModification,
    readOnly,
    snapSize,
    fieldGuideline,
    dragCancelSelectors = [],
  } = props;

  const hasSize = !(size.height === 0 && size.width === 0);

  // TODO : (Juyeong-Byeon) 아래 핸들러 생성 함수는 메모이제이션이 안되어 하위에서 레퍼런스가 계속 바뀐다. 추후 메모이제이션 필요.
  function forwardPointHandler(handler?: (point: AbsolutePoint, event: DraggableEvent) => void) {
    return (event: DraggableEvent, data: DraggableData) =>
      handler?.(createAbsolutePoint(data), event);
  }

  // TODO : (Juyeong-Byeon) 아래 핸들러 생성 함수는 메모이제이션이 안되어 하위에서 레퍼런스가 계속 바뀐다. 추후 메모이제이션 필요.
  function forwardSizeHandler(handler?: (size: AbsoluteSize, event: React.SyntheticEvent) => void) {
    return (event: React.SyntheticEvent, data: ResizeCallbackData) => {
      return handler?.(createAbsoluteSize(data.size), event);
    };
  }

  // TODO: 절대 좌표 세계와 상태 좌표 세계를 분리하는 layer 정해서 구분하기.
  return (
    <Draggable
      {...props}
      bounds="parent"
      cancel={['.react-resizable-handle', ...dragCancelSelectors].filter(Boolean).join(', ')}
      disabled={readOnly || restrictModification || disableDrag}
      position={point}
      onDrag={forwardPointHandler(onPointChange)}
      onStart={forwardPointHandler(onPointChangeStart)}
      onStop={forwardPointHandler(onPointChangeStop)}
    >
      <Box position="absolute" sx={{ '&:hover': { zIndex: 102 } }} zIndex={101}>
        {fieldGuideline && (
          <AbsoluteFieldGuideline
            disabled={!fieldGuideline.showGuideLine}
            offsets={fieldGuideline.guideLineOffset}
            point={point}
            size={size}
            viewport={fieldGuideline.viewport}
          />
        )}
        {hasSize && !readOnly ? (
          <ResizableBox
            {...props}
            draggableOpts={{
              disabled: restrictModification,
              grid: snapSize && [snapSize.width, snapSize.height],
            }}
            height={size.height}
            width={size.width}
            onResize={forwardSizeHandler(onSizeChange)}
            onResizeStart={forwardSizeHandler(onSizeChangeStart)}
            onResizeStop={forwardSizeHandler(onSizeChangeStop)}
          >
            {children}
          </ResizableBox>
        ) : (
          <Box height={size.height} width={size.width}>
            {children}
          </Box>
        )}
      </Box>
    </Draggable>
  );
}
